import { DownloadItem, getAvailableMonths, SelectedMonthContext } from '@e2m-customer-portal/domains';
import { MarketLocationProvider, useFeatureFlags, useMarketLocation } from '@e2m-customer-portal/hooks';
import { CSVDialog, ErrorComponent, SingleSelect } from '@e2m-customer-portal/ui';
import { Typography } from '@mui/material';
import { endOfMonth, startOfMonth } from 'date-fns';
import { ReactElement, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeApiUrl } from '../../../utils';

export interface MonthData {
  id: string;
  date: Date;
  name: string;
}

interface SelectedNameProps {
  options: readonly MonthData[];
  selected: readonly MonthData[];
}

function SelectedName({ selected }: Readonly<SelectedNameProps>): ReactElement<SelectedNameProps> {
  return <>{selected[0]?.name ?? ''}</>;
}

export interface RevenueDownloadContentProps {
  marketLocationId: string;
}

export function RevenueDownloadContent({
  marketLocationId,
}: Readonly<RevenueDownloadContentProps>): ReactElement<RevenueDownloadContentProps> {
  const [t] = useTranslation();
  const [marketLocation, error] = useMarketLocation(marketLocationId);
  const { disableFCRReceiptDownloads } = useFeatureFlags();

  if (!marketLocation || error) {
    return <ErrorComponent error={error} />;
  }

  const availableMonths = getAvailableMonths(
    marketLocation.marketingStart,
    marketLocation.marketingEnd,
    'REVENUE_DOWNLOAD_MONTH',
  );

  const { selectedMonth: selectedTableMonth } = useContext(SelectedMonthContext);

  const [selectedMonth, setSelectedMonth] = useState(selectedTableMonth);

  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <>
      <div>
        {marketLocation.externalName && (
          <Typography variant="body5" className="text-grey-400">
            {marketLocation.name}
          </Typography>
        )}
        <Typography variant="h4">{marketLocation.externalName || marketLocation.name}</Typography>
      </div>

      <SingleSelect
        className="mt-8"
        fullWidth
        options={availableMonths}
        value={selectedMonth}
        onChange={setSelectedMonth}
        SelectComponent={SelectedName}
        label={t('REVENUE_DOWNLOAD_MONTH_LABEL')}
      />

      <Typography variant="body1" className="mt-4">
        {t('REVENUE_DOWNLOAD_HEADER_CHOOSE_DATA')}
      </Typography>

      <MarketLocationProvider value={marketLocation}>
        <CSVDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
      </MarketLocationProvider>

      <div className="mt-4">
        <DownloadItem label={t('REVENUE_DOWNLOAD_LIST_MEASUREMENTS')} onClick={() => setDialogOpen(true)} isGrey />

        <DownloadItem label={t('REVENUE_DOWNLOAD_LIST_RECEIPT_LP')} isHeader />
        <DownloadItem
          label={t('REVENUE_DOWNLOAD_LIST_RECEIPT_LP_FCR')}
          isGrey
          filename={t('REVENUE_DOWNLOAD_FILENAME', {
            type: 'LP',
            product: 'PRL',
            name: marketLocation.name.replaceAll(/\s+/g, '_'),
            start: startOfMonth(selectedMonth.date),
            end: endOfMonth(selectedMonth.date),
          })}
          url={
            disableFCRReceiptDownloads
              ? undefined
              : makeApiUrl('csv-download/lp.csv', {
                  marketLocationId,
                  startOfMonth: selectedMonth.date.toISOString(),
                  controlProductType: 'fcr',
                })
          }
        />
        <DownloadItem
          label={t('REVENUE_DOWNLOAD_LIST_RECEIPT_LP_AFRR')}
          filename={t('REVENUE_DOWNLOAD_FILENAME', {
            type: 'LP',
            product: 'SRL',
            name: marketLocation.name.replaceAll(/\s+/g, '_'),
            start: startOfMonth(selectedMonth.date),
            end: endOfMonth(selectedMonth.date),
          })}
          url={makeApiUrl('csv-download/lp.csv', {
            marketLocationId,
            startOfMonth: selectedMonth.date.toISOString(),
            controlProductType: 'afrr',
          })}
        />
        <DownloadItem
          label={t('REVENUE_DOWNLOAD_LIST_RECEIPT_LP_MFRR')}
          isGrey
          // MFRR does not exist yet
          // filename={t('REVENUE_DOWNLOAD_FILENAME', {
          //   type: 'LP',
          //   product: 'MRL',
          //   name: marketLocation.name.replaceAll(/\s+/g, '_'),
          //   start: startOfMonth(selectedMonth.date),
          //   end: endOfMonth(selectedMonth.date),
          // })}
          // url={makeApiUrl('csv-download/csv-lp', {
          //   marketLocationId,
          //   startOfMonth: selectedMonth.date.toISOString(),
          //   controlProductType: 'mfrr',
          // })}
        />

        <DownloadItem label={t('REVENUE_DOWNLOAD_LIST_RECEIPT_AP')} isHeader />
        <DownloadItem
          label={t('REVENUE_DOWNLOAD_LIST_RECEIPT_AP_AFRR')}
          isGrey
          filename={t('REVENUE_DOWNLOAD_FILENAME', {
            type: 'AP',
            product: 'SRL',
            name: marketLocation.name.replaceAll(/\s+/g, '_'),
            start: startOfMonth(selectedMonth.date),
            end: endOfMonth(selectedMonth.date),
          })}
          url={makeApiUrl('csv-download/ap.csv', {
            marketLocationId,
            startOfMonth: selectedMonth.date.toISOString(),
            controlProductType: 'afrr',
          })}
        />
        <DownloadItem
          label={t('REVENUE_DOWNLOAD_LIST_RECEIPT_AP_MFRR')}
          // MFRR does not exist yet
          // filename={t('REVENUE_DOWNLOAD_FILENAME', {
          //   type: 'AP',
          //   product: 'MRL',
          //   name: marketLocation.name.replaceAll(/\s+/g, '_'),
          //   start: startOfMonth(selectedMonth.date),
          //   end: endOfMonth(selectedMonth.date),
          // })}
          // url={makeApiUrl('csv-download/csv-ap', {
          //   marketLocationId,
          //   startOfMonth: selectedMonth.date.toISOString(),
          //   controlProductType: 'mfrr',
          // })}
        />
      </div>
    </>
  );
}
